import { useMemo, useState } from "react";

type CollectorRow = { source: string; status: string; summary: string; latencyMs: number };

type Coverage = {
  attempted: number;
  ok: number;
  miss: number;
  error: number;
  hitRate: number;
  byCategory: Array<{ category: string; attempted: number; ok: number }>;
  slowest?: Array<{ source: string; latencyMs: number }>;
  failed?: Array<{ source: string; reason: string }>;
};

type Fusion = {
  coverage: Coverage;
  signalStrength: number;
  claims?: Array<{ id: string; kind: string; claim: string; sources: string[]; weight: number; corroboration: number }>;
  conflicts?: Array<{ topic: string; note: string; sources: string[] }>;
};

function statusTone(status: string) {
  if (status === "ok") return "text-cyan";
  if (status === "error") return "text-destructive";
  return "text-muted-foreground";
}

export function SourceMeshPanel({
  collectors,
  fusion,
}: {
  collectors: CollectorRow[];
  fusion?: Fusion | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState<"all" | "ok" | "miss" | "error">("all");

  const coverage = useMemo<Coverage>(() => {
    if (fusion?.coverage) return fusion.coverage;
    const ok = collectors.filter((c) => c.status === "ok").length;
    const error = collectors.filter((c) => c.status === "error").length;
    return {
      attempted: collectors.length,
      ok,
      miss: collectors.length - ok - error,
      error,
      hitRate: collectors.length ? Math.round((ok / collectors.length) * 100) : 0,
      byCategory: [],
    };
  }, [collectors, fusion]);

  const rows = useMemo(
    () =>
      [...collectors]
        .filter((c) => (filter === "all" ? true : c.status === filter))
        .sort((a, b) => a.source.localeCompare(b.source)),
    [collectors, filter],
  );

  return (
    <div className="glass rounded-md overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-panel-border px-3 py-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Source mesh coverage</span>
        <span className="ml-auto font-mono text-[10px] text-cyan">
          {coverage.ok}/{coverage.attempted} returned data · {coverage.hitRate}% hit rate
        </span>
      </div>

      <div className="grid gap-3 p-3 sm:grid-cols-4">
        {[
          ["Attempted", coverage.attempted],
          ["Returned data", coverage.ok],
          ["No data", coverage.miss],
          ["Failed", coverage.error],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-md border border-panel-border bg-background/40 p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{value as number}</div>
          </div>
        ))}
      </div>

      {typeof fusion?.signalStrength === "number" && (
        <div className="px-3 pb-3">
          <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <span>Independent signal strength</span>
            <span className="text-cyan">{fusion.signalStrength}/100</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-background/60">
            <div className="h-full rounded-full bg-cyan/70" style={{ width: `${fusion.signalStrength}%` }} />
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            How much independently corroborated evidence the mesh produced, weighted by source reliability and category spread.
          </p>
        </div>
      )}

      {coverage.byCategory?.length > 0 && (
        <div className="space-y-1.5 border-t border-panel-border px-3 py-3">
          {coverage.byCategory.map((c) => (
            <div key={c.category} className="flex items-center gap-2">
              <span className="w-28 shrink-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.category}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background/60">
                <div
                  className="h-full rounded-full bg-cyan/60"
                  style={{ width: `${c.attempted ? Math.round((c.ok / c.attempted) * 100) : 0}%` }}
                />
              </div>
              <span className="w-14 shrink-0 text-right font-mono text-[10px] tabular-nums text-muted-foreground">
                {c.ok}/{c.attempted}
              </span>
            </div>
          ))}
        </div>
      )}

      {fusion?.conflicts && fusion.conflicts.length > 0 && (
        <div className="border-t border-panel-border px-3 py-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Detected conflicts</div>
          {fusion.conflicts.map((c) => (
            <div key={c.topic} className="mt-1.5 text-xs">
              <span className="font-medium">{c.topic}</span>
              <span className="text-muted-foreground"> — {c.note}</span>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-panel-border px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {(["all", "ok", "miss", "error"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-sm border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                filter === f ? "border-cyan/60 text-cyan" : "border-panel-border text-muted-foreground"
              }`}
            >
              {f}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="ml-auto rounded-sm border border-panel-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
          >
            {expanded ? "Collapse" : `Show all ${collectors.length} sources`}
          </button>
        </div>
      </div>

      <div className="max-h-[420px] divide-y divide-panel-border overflow-y-auto">
        {(expanded ? rows : rows.slice(0, 12)).map((r) => (
          <div key={`${r.source}-${r.latencyMs}`} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm">
            <div className="col-span-12 font-mono text-[11px] text-muted-foreground sm:col-span-4">{r.source}</div>
            <div className={`col-span-3 font-mono text-[11px] uppercase sm:col-span-2 ${statusTone(r.status)}`}>{r.status}</div>
            <div className="col-span-9 text-xs text-muted-foreground sm:col-span-5">{r.summary}</div>
            <div className="col-span-12 text-right font-mono text-[10px] text-muted-foreground sm:col-span-1">{r.latencyMs}ms</div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">No sources match this filter.</div>
        )}
      </div>
    </div>
  );
}
